// @flow
import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { find } from 'lodash';

import { fetchFaqCategories, fetchFaqCategoryQuestions } from '@screens/FAQs/redux/actions';

import type { FaqCategory, FaqQuestion } from '@screens/FAQs/types';

import LazyLoad from 'react-lazyload';

// import { getWindowHeight } from '@lib/helpers';
import { FAQS } from '@routes';
import { withTranslation, Link } from '@i18n';
// import { useScrolledIntoView } from '@hooks';

import type { GlobalState } from '@globalTypes';
// import { useBreakpoint } from '@lib/hooks/useBreakpoint';

import type { Props } from './types';
import {
  StyledCover,
  StyledLinkMore,
  StyledCoverLinkMore,
  StyledCoverTopMaxWidthPadding,
  StyledSubHeadlineText,
  StyledHeadlineTextOne,
  StyledFaqWrapper,
  StyledGridUnit,
  StyledGrid,
} from './styles';

import Question from './components/Question';

const FAQs = ({
  faqs,
  i18n,
  t,
  categoryKey,
  headline,
  color,
  kind = 'patients',
  fetchFaqCategories,
  fetchFaqCategoryQuestions,
  homePage
}: Props): React.Node => {
  const categories: Array<FaqCategory> = kind === 'patients' ? faqs.employeeCategories : faqs.employerCategories;

  const translationKey: string = ((): string => {
    if (kind === 'hospitals') return 'shared.hospitalCategorySlug';

    switch (categoryKey) {
      case 'patient':
        return 'shared.internationalCategorySlug';

      case 'domestic-patient':
        return 'shared.domesticCategorySlug';

      case 'second-opinion':
        return 'shared.secondOpinionSlug';

      default:
        return 'shared.patientCategorySlug';
    }
  })();

  const category: ?FaqCategory = React.useMemo(
    () => find(categories, { attributes: { slug: t(translationKey) } }),
    [categories, t]
  );

  const questions: Array<FaqQuestion> = faqs.questionsByCatSlug[(category?.attributes?.slug)] || [];

  const ref = React.useRef();

  React.useEffect(() => {
    fetchFaqCategories();
  }, [fetchFaqCategories, i18n.language]);

  React.useEffect(() => {
    if (category) fetchFaqCategoryQuestions(category?.attributes?.slug);
  }, [category?.attributes?.slug, fetchFaqCategoryQuestions, i18n.language]);

  return (
    <StyledCover ref={ref} color={color} homePage={homePage}>
      <LazyLoad height="100%" offset={800} once>
        <StyledCoverTopMaxWidthPadding>
          {homePage
            ? <></>
            : (
              <>
                <StyledHeadlineTextOne>{ headline }</StyledHeadlineTextOne>
                <StyledSubHeadlineText>{t('shared.subHeadline')}</StyledSubHeadlineText>
              </>
            )}
          <StyledFaqWrapper>
            <StyledGrid dmb tmb mmb>
              <StyledGridUnit>
                <Question
                  questions={questions}
                  category={category}
                />
              </StyledGridUnit>
            </StyledGrid>
          </StyledFaqWrapper>
          <StyledCoverLinkMore>
            <Link passHref href={FAQS} params={{ tab: kind }}>
              <StyledLinkMore>{t('shared.All')}</StyledLinkMore>
            </Link>
          </StyledCoverLinkMore>
        </StyledCoverTopMaxWidthPadding>
      </LazyLoad>
    </StyledCover>
  );
};

const mapStateToProps = (state: GlobalState) => ({
  faqs: state.faqs
});

const mapDispatchToProps = { fetchFaqCategories, fetchFaqCategoryQuestions };

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withTranslation('FAQs')
)(FAQs);
